import { useEffect, useRef, useState } from 'react';
import type {
  CreditSettings,
  SubmitCreditApplicationResponse,
} from '@nadoutong/contracts';
import {
  CREDIT_APPLICATION_MAX_CYCLE_DAYS,
  CREDIT_APPLICATION_MIN_CYCLE_DAYS,
  CREDIT_APPLICATION_REASON_MAX_LENGTH,
  CREDIT_APPLICATION_REASON_MIN_LENGTH,
  normalizeCreditApplicationReason,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useSubmitCreditApplication,
  useUploadCreditContract,
} from '../api/credit.ts';
import { formatApprovalChainDescription } from './credit/approval-chain.ts';
import { useT } from '../i18n/index.ts';
import {
  attachmentMaxMbLabel,
  bytesToBase64,
  isOversizeAttachment,
} from '../lib/receipts.ts';
import { usePricing } from '../api/pricing.ts';
import { CustomerCompanyLine } from './identity/CustomerCell.tsx';
import { formatCnyCents, parseAmountInputToMinorUnits, usdMicrosToCnyCents } from './formatters.ts';
import { useToast } from './Toast.tsx';
import { Button, Dialog, FormField, Input, Textarea } from '../ui/index.ts';

/**
 * Credit-line application dialog (epic #1033 E4-C1, #1125; router#1999).
 * The salesperson enters a credit limit (CNY) + payment terms (days
 * after statement) and attaches the MANDATORY contract; the contract
 * uploads first, then the application submits referencing its
 * object_key. Submit never activates the line — every application waits
 * for a distinct decider. Payment terms are 1–120 days with no tiers.
 *
 * Amounts are integer CNY cents on the write wire (router#2036). The
 * operator types yuan; parseAmountInputToMinorUnits yields cents.
 */

interface CustomerCreditApplyDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly orgId: string;
  readonly customerEmail: string;
  readonly defaults: CreditSettings;
  readonly onSubmitted?: (response: SubmitCreditApplicationResponse) => void;
}

interface PendingApply {
  readonly contractAttachmentKey: string;
  readonly limitCnyCents: number;
  readonly cycleDays: number;
  readonly reason: string;
}

export function CustomerCreditApplyDialog({
  open,
  onOpenChange,
  orgId,
  customerEmail,
  defaults,
  onSubmitted,
}: CustomerCreditApplyDialogProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  const [limit, setLimit] = useState('');
  const [cycle, setCycle] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // Set once the contract uploaded but the apply call failed — the retry
  // then re-applies with the SAME object_key (never re-uploads).
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (open) {
      return;
    }
    setLimit('');
    setCycle('');
    setReason('');
    setFile(null);
    setValidationError(null);
    setPendingApply(null);
  }, [open]);

  const upload = useUploadCreditContract();
  const apply = useSubmitCreditApplication();
  const busy = upload.isPending || apply.isPending;

  const parsedLimit = parseAmountInputToMinorUnits(limit);
  const limitCnyCents =
    parsedLimit.ok && parsedLimit.minorUnits >= 0 ? parsedLimit.minorUnits : null;
  const defaultLimitCnyCents = usdMicrosToCnyCents(
    defaults.default_limit_usd_micros,
    cnyPerUsd,
  );
  const cycleDays = /^\d+$/.test(cycle.trim()) ? Number.parseInt(cycle.trim(), 10) : null;
  const cycleInRange =
    cycleDays !== null &&
    cycleDays >= CREDIT_APPLICATION_MIN_CYCLE_DAYS &&
    cycleDays <= CREDIT_APPLICATION_MAX_CYCLE_DAYS;

  function finish(_response: SubmitCreditApplicationResponse): void {
    toast.success(t('credit.apply.toast.pending'));
    onSubmitted?.(_response);
    onOpenChange(false);
  }

  function runApply(input: PendingApply): void {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    apply
      .mutateAsync({
        orgId,
        requestedLimitCnyCents: input.limitCnyCents,
        requestedCycleDays: input.cycleDays,
        contractAttachmentKey: input.contractAttachmentKey,
        reason: input.reason,
      })
      .then((response) => finish(response))
      .catch(() => {
        // Contract already uploaded — from here the retry re-applies with
        // the same object_key rather than re-uploading.
        setPendingApply(input);
      })
      .finally(() => {
        submitInFlightRef.current = false;
      });
  }

  function handleSubmit(): void {
    if (busy) {
      return;
    }
    if (pendingApply !== null) {
      setValidationError(null);
      runApply(pendingApply);
      return;
    }
    if (limitCnyCents === null) {
      setValidationError(t('credit.apply.error.limit'));
      return;
    }
    if (cycleDays === null || !cycleInRange) {
      setValidationError(
        t('credit.apply.error.cycle', {
          min: String(CREDIT_APPLICATION_MIN_CYCLE_DAYS),
          max: String(CREDIT_APPLICATION_MAX_CYCLE_DAYS),
        }),
      );
      return;
    }
    const normalizedReason = normalizeCreditApplicationReason(reason);
    if (normalizedReason === null) {
      setValidationError(
        t('credit.apply.error.reason', {
          min: String(CREDIT_APPLICATION_REASON_MIN_LENGTH),
          max: String(CREDIT_APPLICATION_REASON_MAX_LENGTH),
        }),
      );
      return;
    }
    if (file === null) {
      setValidationError(t('credit.apply.error.contract_required'));
      return;
    }
    if (isOversizeAttachment(file.size)) {
      setValidationError(t('credit.apply.error.oversize', { mb: attachmentMaxMbLabel() }));
      return;
    }
    setValidationError(null);

    const evidence = file;
    submitInFlightRef.current = true;
    void evidence
      .arrayBuffer()
      .then((buffer) =>
        upload.mutateAsync({
          orgId,
          fileName: evidence.name,
          contentType: evidence.type.length > 0 ? evidence.type : 'application/octet-stream',
          dataBase64: bytesToBase64(new Uint8Array(buffer)),
        }),
      )
      .then((uploaded) => {
        submitInFlightRef.current = false;
        runApply({
          contractAttachmentKey: uploaded.attachment.object_key,
          limitCnyCents,
          cycleDays,
          reason: normalizedReason,
        });
      })
      .catch(() => {
        submitInFlightRef.current = false;
        /* upload.error renders below */
      });
  }

  function handleFileChange(nextFile: File | null): void {
    if (nextFile !== null && isOversizeAttachment(nextFile.size)) {
      setValidationError(t('credit.apply.error.oversize', { mb: attachmentMaxMbLabel() }));
      setFile(null);
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = '';
      }
      return;
    }
    setValidationError(null);
    setFile(nextFile);
  }

  const uploadError =
    upload.error !== null
      ? upload.error instanceof OpsApiError
        ? `${upload.error.code}: ${upload.error.message}`
        : upload.error.message
      : null;
  const applyError =
    apply.error !== null
      ? apply.error instanceof OpsApiError
        ? `${apply.error.code}: ${apply.error.message}`
        : apply.error.message
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!busy) {
          onOpenChange(next);
        }
      }}
      title={t('credit.apply.title')}
      description={
        <span data-testid="credit-apply-description">
          {formatApprovalChainDescription('credit_application', t)}
        </span>
      }
      closeLabel={t('common.close')}
      testId="credit-apply-dialog"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="credit-apply-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={busy}
            data-testid="credit-apply-confirm"
          >
            {pendingApply !== null ? t('credit.apply.retry') : t('credit.apply.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 rounded-lg bg-well px-3 py-2.5">
          <span className="text-label text-paper-low">{t('credit.apply.customer')}</span>
          <CustomerCompanyLine orgId={orgId} />
          <span className="text-body font-medium text-paper">{customerEmail}</span>
          <span className="font-mono text-micro text-paper-low">{orgId}</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t('credit.apply.limit_label')}
            fieldId="credit-apply-limit"
            required
            hint={t('credit.apply.limit_hint', {
              max: defaultLimitCnyCents === null ? '—' : formatCnyCents(defaultLimitCnyCents, false),
            })}
          >
            <Input
              id="credit-apply-limit"
              value={limit}
              onChange={(event) => setLimit(event.currentTarget.value)}
              placeholder="0.00"
              inputMode="decimal"
              disabled={pendingApply !== null}
              data-testid="credit-apply-limit"
            />
          </FormField>
          <FormField
            label={t('credit.apply.cycle_label')}
            fieldId="credit-apply-cycle"
            required
            hint={t('credit.apply.cycle_hint', {
              min: String(CREDIT_APPLICATION_MIN_CYCLE_DAYS),
              max: String(CREDIT_APPLICATION_MAX_CYCLE_DAYS),
            })}
          >
            <Input
              id="credit-apply-cycle"
              value={cycle}
              onChange={(event) => setCycle(event.currentTarget.value)}
              placeholder={String(defaults.default_grace_days)}
              inputMode="numeric"
              disabled={pendingApply !== null}
              data-testid="credit-apply-cycle"
            />
          </FormField>
        </div>

        <FormField
          label={t('credit.apply.reason_label')}
          fieldId="credit-apply-reason"
          required
          hint={t('credit.apply.reason_hint', {
            min: String(CREDIT_APPLICATION_REASON_MIN_LENGTH),
            max: String(CREDIT_APPLICATION_REASON_MAX_LENGTH),
          })}
        >
          <Textarea
            id="credit-apply-reason"
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            rows={3}
            disabled={pendingApply !== null}
            data-testid="credit-apply-reason"
          />
        </FormField>

        <FormField
          label={t('credit.apply.contract_label')}
          fieldId="credit-apply-contract"
          required
          hint={t('credit.apply.contract_hint', { mb: attachmentMaxMbLabel() })}
        >
          <input
            id="credit-apply-contract"
            ref={fileInputRef}
            type="file"
            onChange={(event) => handleFileChange(event.currentTarget.files?.[0] ?? null)}
            disabled={pendingApply !== null}
            className="block w-full rounded-md border border-line-soft bg-canvas px-3 py-2 text-dense text-paper-mid file:mr-3 file:rounded file:border-0 file:bg-raised file:px-2.5 file:py-1 file:text-label file:text-paper"
            data-testid="credit-apply-contract"
          />
        </FormField>

        {limitCnyCents !== null && cycleInRange ? (
          <p
            data-testid="credit-apply-needs-approval"
            className="m-0 rounded-lg border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense text-warn"
          >
            {t('credit.apply.hint.needs_approval')}
          </p>
        ) : null}

        {pendingApply !== null ? (
          <p
            role="alert"
            data-testid="credit-apply-pending"
            className="m-0 rounded-lg border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense text-warn"
          >
            {t('credit.apply.pending_retry')}
          </p>
        ) : null}
        {validationError !== null ? (
          <p
            role="alert"
            data-testid="credit-apply-validation-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {validationError}
          </p>
        ) : null}
        {uploadError !== null ? (
          <p
            role="alert"
            data-testid="credit-apply-upload-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {t('credit.apply.error.upload_failed', { detail: uploadError })}
          </p>
        ) : null}
        {applyError !== null ? (
          <p
            role="alert"
            data-testid="credit-apply-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {applyError}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
